/**
 * A conferência do nome do circuito DENTRO do painel de cadastro — a peça que a
 * tela de Cadastros e a gaveta do gráfico (e do editor de ligações) usam. Por
 * isso ela vale nos dois lugares: foi a decisão de 26/09/2026 (Q3), para que
 * renomear circuito deixe de gravar sem conferir.
 *
 * A consulta é a mesma do editor (`GET api/pinouts/grafo/nome`), com o próprio
 * circuito fora da conta: renomeado, ele não é mais o que era. As regras estão
 * em `conferirRenomear` (regras do editor), testadas sem navegador.
 */

import { useEffect } from "react"
import { usarConferenciaDoNome } from "@/api/editorDeLigacoes"
import { conferirRenomear } from "@/telas/pinout/grafico/editor/regras"

export type EstadoDoNome = { bloqueio: string | null; confirmar: boolean }

export function ConferenciaDoCircuito({ projetoId, circuitoId, nome, original, confirmado, aoConfirmar, aoMudarEstado }: {
  projetoId: number
  /** 0 ao incluir. */
  circuitoId: number
  nome: string
  /** O nome gravado; `null` ao incluir. */
  original: string | null
  confirmado: boolean
  aoConfirmar: (v: boolean) => void
  aoMudarEstado: (e: EstadoDoNome) => void
}) {
  const mudou = original === null || nome.trim() !== original.trim()
  const { conferencia } = usarConferenciaDoNome(projetoId, mudou ? nome : "", circuitoId)
  const estado = conferirRenomear(mudou ? conferencia : null, nome, mudou ? original : nome)

  useEffect(() => { aoMudarEstado(estado) }, [estado.bloqueio, estado.confirmar])

  if (!mudou || nome.trim() === "") return null
  if (!conferencia) return <p style={caixa("var(--surface2)", "var(--txt3)")}>Conferindo o nome…</p>
  if (estado.bloqueio) return <p role="alert" style={caixa("var(--erro-soft)", "var(--erro-on-soft)")}>{estado.bloqueio}</p>

  const liga = [...conferencia.alcanca, ...conferencia.alcancadoPor.filter((a) => !conferencia.alcanca.some((b) => b.circuitoId === a.circuitoId))]
  if (liga.length === 0) return <p style={caixa("var(--ok-soft)", "var(--ok-on-soft)")}>Nome livre: não liga em nenhum outro circuito.</p>
  return (
    <div style={{ ...caixa("var(--aviso-soft)", "var(--aviso-on-soft)"), display: "flex", flexDirection: "column", gap: 3 }}>
      <span>Pelo começo do nome, o desenho também vai ligar este fio em:</span>
      {liga.slice(0, 10).map((c) => (
        <span key={c.circuitoId}>
          • <code style={{ fontFamily: "var(--fm)" }}>{c.descricao}</code> ({c.pinos} ponta{c.pinos === 1 ? "" : "s"}{c.onde.length > 0 ? `: ${c.onde.join(", ")}` : ""})
        </span>
      ))}
      {liga.length > 10 && <span>… e mais {liga.length - 10}.</span>}
      <label style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 3 }}>
        <input type="checkbox" checked={confirmado} onChange={(e) => aoConfirmar(e.target.checked)} />
        <b>É isso mesmo: o fio também liga nesses circuitos.</b>
      </label>
    </div>
  )
}

function caixa(fundo: string, texto: string): React.CSSProperties {
  return { margin: "12px 0 0", padding: "9px 11px", borderRadius: 8, fontSize: 11.5, lineHeight: 1.5, background: fundo, color: texto }
}
